// LampContext.jsx
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const LampContext = createContext(null);

export function LampProvider({ children }) {
  const location = useLocation();

  // ✅ IMPORTANT: On refresh, if route is NOT home => lamp should be ON.
  // If on Home, we want to respect the user's last choice (persisted) or default to OFF.
  const [isLampOn, setIsLampOn] = useState(() => {
    if (location.pathname !== "/") {
      return true;
    }
    // Check local storage for persistent preference
    const saved = localStorage.getItem("protek_lamp_state");
    return saved ? JSON.parse(saved) : false; // Default off if never visited
  });

  // Persist state changes
  useEffect(() => {
    localStorage.setItem("protek_lamp_state", JSON.stringify(isLampOn));
  }, [isLampOn]);

  const scrollYRef = useRef(0);

  useEffect(() => {
    const isHome = location.pathname === "/";
    const shouldLock = isHome && !isLampOn;

    const html = document.documentElement;
    const body = document.body;

    const unlock = () => {
      html.classList.remove("lamp-off");
      body.classList.remove("lamp-off");

      body.style.overflow = "";
      body.style.position = "";
      body.style.width = "";
      body.style.top = "";
      html.style.overflow = "";

      // restore scroll if we locked before
      if (scrollYRef.current) {
        window.scrollTo(0, scrollYRef.current);
        scrollYRef.current = 0;
      }
    };

    if (shouldLock) {
      scrollYRef.current = window.scrollY || 0;

      html.classList.add("lamp-off");
      body.classList.add("lamp-off");

      body.style.overflow = "hidden";
      body.style.position = "fixed";
      body.style.width = "100%";
      body.style.top = `-${scrollYRef.current}px`;
      html.style.overflow = "hidden";
    } else {
      unlock();
    }

    return () => unlock();
  }, [isLampOn, location.pathname]);

  return (
    <LampContext.Provider value={{ isLampOn, setIsLampOn }}>
      {children}
    </LampContext.Provider>
  );
}

export function useLamp() {
  const ctx = useContext(LampContext);
  if (!ctx) throw new Error("useLamp must be used within LampProvider");
  return ctx;
}
